import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { adminError, isSessionRequired } from "../lib/jamoraAdmin";
import { useAdminSession } from "../components/jamora-admin/SessionProvider";

// A cursor-paged list of the Jamora administration API. `phase`: loading | ready | more | error. Changing the filters restarts from the
// first page; « Voir plus » appends the next one; a newer request always wins over an older one (same rule as useAdminFetch).
export default function useAdminList(path, params, { enabled = true } = {}) {
  const adminSession = useAdminSession();
  const [state, setState] = useState({ phase: enabled ? "loading" : "idle", items: [], cursor: null, error: "", forKey: "" });
  const seq = useRef(0);
  const key = JSON.stringify(params || {});
  const current = `${path}|${key}`;

  const load = useCallback(async (cursor) => {
    if (!enabled || !path) return;
    const mine = ++seq.current;
    setState((s) => ({ ...s, phase: cursor ? "more" : "loading", error: "", items: cursor ? s.items : [], forKey: current }));
    try {
      const clean = Object.fromEntries(Object.entries(JSON.parse(key)).filter(([, v]) => v !== "" && v !== null && v !== undefined));
      const { data } = await api.get(`/jamora-admin${path}`, { params: { limit: 50, ...clean, ...(cursor ? { cursor } : {}) } });
      if (mine !== seq.current) return;
      setState((s) => ({ phase: "ready", items: cursor ? [...s.items, ...data.items] : data.items, cursor: data.next_cursor || null, error: "", forKey: current }));
      adminSession?.touch();
    } catch (e) {
      if (mine !== seq.current) return;
      if (isSessionRequired(e)) { adminSession?.expire(); return; }
      setState((s) => ({ ...s, phase: "error", error: adminError(e) }));
    }
  }, [enabled, path, key, current]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(null); return () => { seq.current += 1; }; }, [load]);

  // Rows of another list (the previous tab or filter, until the new request starts) are never shown with the new list's columns.
  const stale = enabled && state.forKey !== current;
  return { ...state, ...(stale ? { phase: "loading", items: [], cursor: null, error: "" } : {}), reload: () => load(null), more: () => state.cursor && load(state.cursor) };
}
